import { useEffect, useMemo, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Printer, Download, Upload, FileText, Eye, Trash2, Sliders, CheckCircle2, AlertCircle, LayoutGrid, Save } from "lucide-react";
import LayoutEditorModal from "@/components/LayoutEditorModal";
import { SAMPLE_ID } from "@/lib/ticketTypes";

const PRINT_TYPES = ["REGULAR", "VIP", "EMPLOYEE"];
import SheetLayoutModal, { SheetVisual } from "@/components/SheetLayoutModal";
import { CARD_ASPECT as TICKET_ASPECT, CARD_W_IN, CARD_H_IN, SHEET_5871, cardLayout, artStyle } from "@/lib/avery5871";

const EMPTY_TPL = { active: null, versions: [] };

function StatusPill({ status }) {
  const map = {
    ACTIVE:   "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
    MISSING:  "bg-red-500/20 text-red-300 border-red-500/30",
    INACTIVE: "bg-slate-700/40 text-slate-300 border-slate-600",
  };
  const Icon = status === "ACTIVE" ? CheckCircle2 : AlertCircle;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] tracking-widest uppercase font-bold px-2 py-1 rounded border ${map[status] || map.INACTIVE}`} data-testid={`status-${status}`}>
      <Icon size={11} /> {status}
    </span>
  );
}

function CompositePreview({ active, ticketType }) {
  if (!active) {
    return (
      <div className="w-full rounded-lg border border-dashed border-slate-700 flex items-center justify-center text-slate-500 text-sm bg-slate-950/40" style={{ aspectRatio: TICKET_ASPECT }}>
        No artwork uploaded
      </div>
    );
  }
  const token = localStorage.getItem("fb_token") || "";
  const url = `${API}/print/templates/${active.id}/artwork?token=${encodeURIComponent(token)}`;
  const L = cardLayout(active.layout);
  const pctX = (v, d) => ((Number(v ?? d) / CARD_W_IN) * 100) + "%";
  const pctY = (v, d) => ((Number(v ?? d) / CARD_H_IN) * 100) + "%";
  return (
    <div className="relative w-full max-w-[360px] mx-auto rounded-lg overflow-hidden border border-slate-700 bg-white" style={{ aspectRatio: TICKET_ASPECT }} data-testid={`preview-${ticketType}`}>
      <img src={url} alt="" style={artStyle(L)} />
      <div className="absolute flex items-center justify-center"
        style={{
          left: pctX(L.barcode_x, 3.1), top: pctY(L.barcode_y, 0.2),
          width: pctX(L.barcode_w, 2.2), height: pctY(L.barcode_h, 0.65),
          background: L.barcode_white_bg === false ? "rgba(255,255,255,0.4)" : "rgba(255,255,255,0.95)",
        }}>
        <div className="text-black text-[6px] font-mono">|||||||||||||||</div>
      </div>
      <div className="absolute text-black font-bold" style={{
        left: pctX(L.ticket_id_x, 3.1), top: pctY(L.ticket_id_y, 0.87),
        fontSize: "0.55rem",
      }}>{SAMPLE_ID[ticketType]}</div>
    </div>
  );
}

function TemplateCard({ ticketType, data, onUpload, onEdit, onRemove, onActivate }) {
  const active = data.active;
  const versions = data.versions || [];
  const olderVersions = versions.filter((v) => !v.is_active);
  const status = active ? "ACTIVE" : "MISSING";

  return (
    <div className="rounded-2xl bg-[#131D33] border border-slate-800 p-4 sm:p-5 overflow-hidden" data-testid={`tpl-card-${ticketType}`}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0 flex-1">
          <div className="font-display text-xl font-black text-white tracking-tight">{ticketType} TEMPLATE</div>
          <div className="text-[10px] tracking-widest uppercase text-slate-500 mt-0.5 truncate">
            {active ? `Active v${active.version} · ${active.artwork_filename}` : "Upload artwork to enable production printing"}
          </div>
        </div>
        <StatusPill status={status} />
      </div>

      <CompositePreview active={active} ticketType={ticketType} />

      <div className="flex flex-wrap gap-2 mt-3">
        <label className="inline-flex items-center gap-1 h-9 px-3 rounded bg-orange-500 hover:bg-orange-600 text-white font-bold text-xs cursor-pointer transition" data-testid={`upload-${ticketType}`}>
          <Upload size={14} /> {active ? "Replace Artwork" : "Upload Artwork"}
          <input type="file" accept="image/png,image/jpeg" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }} />
        </label>
        {active && (
          <>
            <Button size="sm" variant="outline" className="bg-slate-900 border-slate-700 text-slate-200 h-9" onClick={() => onEdit(active)} data-testid={`configure-${ticketType}`}>
              <Sliders size={14} className="mr-1" /> Configure Layout
            </Button>
            <a href={`${API}/print/templates/${active.id}/artwork?token=${encodeURIComponent(localStorage.getItem("fb_token") || "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 h-9 px-3 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs" data-testid={`preview-open-${ticketType}`}>
              <Eye size={14} /> Preview
            </a>
            <Button size="sm" variant="outline" className="bg-slate-900 border-red-900/50 text-red-300 hover:text-red-200 h-9" onClick={() => onRemove(active.id, true)} data-testid={`remove-${ticketType}`}>
              <Trash2 size={14} className="mr-1" /> Remove
            </Button>
          </>
        )}
      </div>

      {olderVersions.length > 0 && (
        <details className="mt-3">
          <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-300">Older versions ({olderVersions.length})</summary>
          <div className="mt-2 space-y-1">
            {olderVersions.map((v) => (
              <div key={v.id} className="flex items-center justify-between text-xs text-slate-400 bg-slate-900/60 rounded px-2 py-1.5">
                <div className="truncate">v{v.version} · {v.artwork_filename}</div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => onActivate(v.id)} className="text-emerald-300 hover:underline" data-testid={`activate-${v.id}`}>Activate</button>
                  <button onClick={() => onRemove(v.id, false)} className="text-red-400 hover:underline" data-testid={`remove-old-${v.id}`}>Remove</button>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

export default function PrintTickets() {
  const [type, setType] = useState("ALL");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [startPos, setStartPos] = useState(1);
  const [includeGuest, setIncludeGuest] = useState(false);
  const [calX, setCalX] = useState(0);
  const [calY, setCalY] = useState(0);
  const [busy, setBusy] = useState(false);
  const [lastJob, setLastJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [templates, setTemplates] = useState({ REGULAR: EMPTY_TPL, VIP: EMPTY_TPL, EMPLOYEE: EMPTY_TPL });
  const [editing, setEditing] = useState(null);
  const [sheetEditing, setSheetEditing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sheetLayout, setSheetLayout] = useState(SHEET_5871);
  const [profiles, setProfiles] = useState([]);
  const [newProfileName, setNewProfileName] = useState("");

  const token = localStorage.getItem("fb_token") || "";

  const loadJobs = () => api.get("/print/jobs").then((r) => setJobs(r.data.items)).catch(() => {});
  const loadTemplates = () => api.get("/print/templates").then((r) => {
    setTemplates({ REGULAR: r.data?.REGULAR || EMPTY_TPL, VIP: r.data?.VIP || EMPTY_TPL, EMPLOYEE: r.data?.EMPLOYEE || EMPTY_TPL });
  }).catch(() => {});
  const loadSheet = () => api.get("/print/sheet-settings").then((r) => {
    setSheetLayout(r.data?.sheet_layout || SHEET_5871);
  }).catch(() => {});
  const loadProfiles = () => api.get("/print/profiles").then((r) => setProfiles(r.data?.items || [])).catch(() => {});

  useEffect(() => { loadJobs(); loadTemplates(); loadSheet(); loadProfiles(); }, []);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const buildJobBody = (test = false) => ({
    ticket_type_filter: type,
    range_start: rangeStart || null,
    range_end: rangeEnd || null,
    start_position: Number(startPos),
    include_guest_name: includeGuest,
    calibration_x_in: Number(calX),
    calibration_y_in: Number(calY),
    test_alignment: test,
    sheet_layout: sheetLayout,
  });

  const generate = async (test = false) => {
    setBusy(true);
    try {
      const { data } = await api.post("/print/generate", buildJobBody(test));
      setLastJob(data);
      toast.success(`Ready · ${data.ticket_count} tickets · ${data.sheets} sheet(s) · ${data.orientation}`);
      loadJobs();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const doPreview = async () => {
    setPreviewLoading(true);
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    try {
      const res = await api.post("/print/preview", buildJobBody(false), { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setPreviewUrl(url);
    } catch (e) {
      try {
        const txt = await e.response?.data?.text?.();
        if (txt) {
          try { const j = JSON.parse(txt); toast.error(j.detail || formatApiError(e)); }
          catch { toast.error(txt); }
        } else { toast.error(formatApiError(e)); }
      } catch { toast.error(formatApiError(e)); }
    } finally { setPreviewLoading(false); }
  };

  const uploadArtwork = async (ticketType, file) => {
    if (!/^image\/(png|jpe?g)$/i.test(file.type)) { toast.error("PNG or JPG only"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Max 8 MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post("/print/templates", {
          ticket_type: ticketType, artwork_b64: reader.result,
          artwork_filename: file.name, artwork_mime: file.type,
        });
        toast.success(`${ticketType} artwork uploaded (new version activated)`);
        loadTemplates();
      } catch (e) { toast.error(formatApiError(e)); }
    };
    reader.readAsDataURL(file);
  };

  const removeArtwork = async (tplId, isActive) => {
    const msg = isActive
      ? "Remove the ACTIVE template? The most recent older version (if any) will be reactivated automatically."
      : "Remove this template version?";
    if (!window.confirm(msg)) return;
    try { await api.delete(`/print/templates/${tplId}`); toast.success("Template removed"); loadTemplates(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const activate = async (tplId) => {
    try { await api.post(`/print/templates/${tplId}/activate`); toast.success("Version activated"); loadTemplates(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const saveProfile = async () => {
    if (!newProfileName.trim()) { toast.error("Give the profile a name"); return; }
    try {
      await api.post("/print/profiles", { name: newProfileName.trim(), sheet_layout: sheetLayout, calibration_x_in: Number(calX), calibration_y_in: Number(calY) });
      setNewProfileName(""); loadProfiles(); toast.success("Profile saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const applyProfile = (p) => {
    setSheetLayout(p.sheet_layout);
    setCalX(p.calibration_x_in);
    setCalY(p.calibration_y_in);
    toast.success(`Applied profile · ${p.name}`);
  };
  const deleteProfile = async (id) => {
    if (!window.confirm("Delete this profile?")) return;
    try { await api.delete(`/print/profiles/${id}`); loadProfiles(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const slotsPerSheet = Math.max(1, (sheetLayout.cols || 1) * (sheetLayout.rows || 1));
  const positions = useMemo(() => Array.from({ length: slotsPerSheet }, (_, i) => i + 1), [slotsPerSheet]);
  // Clamp start position if slots shrink
  useEffect(() => { if (startPos > slotsPerSheet) setStartPos(1); }, [slotsPerSheet, startPos]);

  const downloadUrl = (job) => `${API}/print/download/${job.job_id || job.id}?token=${encodeURIComponent(token)}`;

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs tracking-[0.3em] uppercase text-orange-400 font-bold">Avery 5871 · Business Cards</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight">PRINT TICKETS</h1>
        <p className="text-slate-400 text-sm mt-2">
          US Letter portrait · 10 cards (2 × 5) of 3.5" × 2" · Print at 100% actual size — production PDFs are blocked until required templates are active.
        </p>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {PRINT_TYPES.map((tt) => (
          <TemplateCard key={tt} ticketType={tt} data={templates[tt] || EMPTY_TPL}
            onUpload={(file) => uploadArtwork(tt, file)}
            onEdit={(tpl) => setEditing(tpl)}
            onRemove={removeArtwork} onActivate={activate} />
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-[#131D33] border border-slate-800 rounded-2xl p-5 space-y-4">
          <div className="text-xs tracking-[0.3em] uppercase text-orange-400 font-bold">Print Job</div>

          {/* Sheet Orientation */}
          <div className="rounded-lg bg-[#0B1120] border border-slate-800 p-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <Label className="text-xs tracking-widest uppercase text-slate-400">Sheet · Avery 5871 (Portrait)</Label>
              <Button size="sm" variant="outline" className="bg-slate-900 border-slate-700 text-slate-200" onClick={() => setSheetEditing(true)} data-testid="configure-sheet">
                <LayoutGrid size={14} className="mr-1" /> Configure Sheet Layout
              </Button>
            </div>
            <div className="mt-3">
              <SheetVisual layout={sheetLayout} calX={calX} calY={calY} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs tracking-widest uppercase text-slate-400">Ticket Selection</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="bg-[#0B1120] border-slate-700 text-white" data-testid="print-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Tickets</SelectItem>
                  <SelectItem value="REGULAR">Regular Only</SelectItem>
                  <SelectItem value="VIP">VIP Only</SelectItem>
                  <SelectItem value="EMPLOYEE">Employee Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase text-slate-400">From # (sequence)</Label>
              <Input value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} placeholder="1  (or REG-0001)" className="bg-[#0B1120] border-slate-700 text-white font-mono-fb" data-testid="print-range-start" />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase text-slate-400">To # (sequence)</Label>
              <Input value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} placeholder="10  (or REG-0010)" className="bg-[#0B1120] border-slate-700 text-white font-mono-fb" data-testid="print-range-end" />
            </div>
          </div>

          <div>
            <Label className="text-xs tracking-widest uppercase text-slate-400">Start Position (first sheet) · {slotsPerSheet} slot{slotsPerSheet !== 1 ? "s" : ""}</Label>
            <div className="mt-2 grid gap-1" style={{ gridTemplateColumns: `repeat(${Math.max(1, sheetLayout.cols)}, minmax(0, 1fr))` }}>
              {positions.map((p) => (
                <button key={p} onClick={() => setStartPos(p)} data-testid={`pos-${p}`}
                  className={`h-10 rounded font-display font-black text-sm transition ${startPos === p ? "bg-orange-500 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}>
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center justify-between rounded-lg bg-[#0B1120] border border-slate-800 px-3 py-2">
              <Label>Include Guest Name</Label>
              <Switch checked={includeGuest} onCheckedChange={setIncludeGuest} data-testid="print-include-guest" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] tracking-widest uppercase text-slate-400">Calibration X (in)</Label>
                <Input type="number" step="0.01" min="-0.5" max="0.5" value={calX} onChange={(e) => setCalX(e.target.value)} className="bg-[#0B1120] border-slate-700 text-white" data-testid="print-cal-x" />
              </div>
              <div>
                <Label className="text-[10px] tracking-widest uppercase text-slate-400">Calibration Y (in)</Label>
                <Input type="number" step="0.01" min="-0.5" max="0.5" value={calY} onChange={(e) => setCalY(e.target.value)} className="bg-[#0B1120] border-slate-700 text-white" data-testid="print-cal-y" />
              </div>
            </div>
          </div>

          {/* Print Settings Summary */}
          <div className="rounded-lg bg-slate-950/60 border border-slate-800 p-3 text-xs text-slate-300 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="print-summary">
            <div><div className="text-[9px] uppercase tracking-widest text-slate-500">Paper</div>Avery 5871 · US Letter</div>
            <div><div className="text-[9px] uppercase tracking-widest text-slate-500">Orientation</div><span className="text-orange-300 font-bold">{sheetLayout.orientation}</span></div>
            <div><div className="text-[9px] uppercase tracking-widest text-slate-500">Ticket Size</div>{sheetLayout.ticket_w}" × {sheetLayout.ticket_h}"</div>
            <div><div className="text-[9px] uppercase tracking-widest text-slate-500">Tickets / Sheet</div>{slotsPerSheet}</div>
            <div><div className="text-[9px] uppercase tracking-widest text-slate-500">Scale</div>100% Actual Size</div>
            <div><div className="text-[9px] uppercase tracking-widest text-slate-500">Calibration</div>X {Number(calX).toFixed(2)}" / Y {Number(calY).toFixed(2)}"</div>
            <div><div className="text-[9px] uppercase tracking-widest text-slate-500">Content Rotation</div>{sheetLayout.content_rotation || 0}°</div>
            <div><div className="text-[9px] uppercase tracking-widest text-slate-500">Start Position</div>#{startPos}</div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={() => generate(false)} disabled={busy} className="bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold" data-testid="print-generate">
              <Printer size={16} className="mr-1" /> GENERATE PRINT PDF
            </Button>
            <Button onClick={() => generate(true)} disabled={busy} variant="outline" className="bg-slate-900 border-slate-700 text-slate-200" data-testid="print-test">
              <FileText size={16} className="mr-1" /> Test Alignment Sheet
            </Button>
          </div>

          {lastJob && (
            <div className="mt-3 rounded-xl bg-[#0B1120] border border-emerald-500/30 p-4">
              <div className="text-emerald-300 font-bold mb-2">✓ PRINT JOB READY</div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm text-slate-300">
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Tickets</div><b>{lastJob.ticket_count}</b></div>
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Regular</div><b>{lastJob.regular_count}</b></div>
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">VIP</div><b>{lastJob.vip_count}</b></div>
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Employee</div><b data-testid="print-job-employee-count">{lastJob.employee_count ?? 0}</b></div>
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Sheets</div><b>{lastJob.sheets}</b></div>
              </div>
              <div className="mt-3 rounded bg-slate-950/60 border border-amber-500/30 p-3 text-xs text-amber-200" data-testid="print-instructions">
                <b className="text-amber-300 uppercase tracking-widest">Printer Settings</b><br/>
                Paper: <b>US Letter</b> · Orientation: <b>{lastJob.orientation}</b> · Scale: <b>100% / Actual Size</b><br/>
                Do <b>NOT</b> select "Fit to Page" or "Shrink Oversized Pages" — the printer must use Actual Size for the Avery slots to line up.
              </div>
              <a href={downloadUrl(lastJob)} className="inline-flex items-center gap-2 mt-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold px-4 py-2 rounded-lg" download data-testid="print-download-link">
                <Download size={16} /> Download {lastJob.filename}
              </a>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-[#131D33] border border-slate-800 rounded-2xl p-5 space-y-3">
            <div className="text-xs tracking-[0.3em] uppercase text-orange-400 font-bold">Preview Print Job</div>
            <p className="text-xs text-slate-500">Renders the first Avery 5871 sheet exactly as it will print (8.5" × 11", 2 × 5 cards).</p>
            <Button onClick={doPreview} disabled={previewLoading} className="w-full bg-slate-800 hover:bg-slate-700 text-white" data-testid="print-preview-btn">
              <Eye size={16} className="mr-2" /> {previewLoading ? "Rendering…" : "Preview First Sheet"}
            </Button>
            {previewUrl ? (
              <iframe key={sheetLayout.orientation} src={previewUrl} className="w-full bg-white rounded" style={{ aspectRatio: sheetLayout.orientation === "LANDSCAPE" ? "11 / 8.5" : "8.5 / 11" }} title="Print preview" data-testid="print-preview-frame" />
            ) : (
              <div className="rounded border border-dashed border-slate-700 flex items-center justify-center text-slate-500 text-xs p-6" style={{ aspectRatio: sheetLayout.orientation === "LANDSCAPE" ? "11 / 8.5" : "8.5 / 11" }} data-testid="preview-empty">
                Click preview to render the first sheet ({sheetLayout.orientation})
              </div>
            )}
          </div>

          <div className="bg-[#131D33] border border-slate-800 rounded-2xl p-5 space-y-3">
            <div className="text-xs tracking-[0.3em] uppercase text-orange-400 font-bold">Print Profiles</div>
            <div className="flex gap-2">
              <Input value={newProfileName} onChange={(e) => setNewProfileName(e.target.value)} placeholder="e.g. Office Printer" className="bg-[#0B1120] border-slate-700 text-white" data-testid="profile-name" />
              <Button onClick={saveProfile} className="bg-orange-500 hover:bg-orange-600 text-white font-bold" data-testid="save-profile">
                <Save size={14} className="mr-1" /> Save
              </Button>
            </div>
            {profiles.length === 0 && <div className="text-xs text-slate-500">No profiles saved yet.</div>}
            {profiles.map((p) => (
              <div key={p.id} className="flex items-center justify-between text-xs bg-slate-950/60 rounded px-2 py-1.5" data-testid={`profile-${p.id}`}>
                <div className="min-w-0 flex-1 truncate">
                  <div className="text-slate-200 font-bold">{p.name}</div>
                  <div className="text-slate-500 text-[10px] truncate">{p.sheet_layout?.orientation} · cal X {p.calibration_x_in}" · Y {p.calibration_y_in}"</div>
                </div>
                <div className="flex gap-2 shrink-0 ml-2">
                  <button onClick={() => applyProfile(p)} className="text-emerald-300 hover:underline" data-testid={`apply-profile-${p.id}`}>Apply</button>
                  <button onClick={() => deleteProfile(p.id)} className="text-red-400 hover:underline" data-testid={`delete-profile-${p.id}`}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-[#131D33] border border-slate-800 rounded-2xl">
        <div className="px-4 py-3 border-b border-slate-800 flex justify-between items-center">
          <div className="font-display text-xl font-bold tracking-tight">PRINT HISTORY</div>
          <span className="text-xs text-slate-500">{jobs.length} jobs</span>
        </div>
        <div className="divide-y divide-slate-800">
          {jobs.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">No print jobs yet.</div>}
          {jobs.slice(0, 30).map((j) => (
            <div key={j.id} className="grid grid-cols-2 md:grid-cols-6 px-4 py-3 items-center text-sm" data-testid={`job-${j.id}`}>
              <div className="md:col-span-2 text-slate-400">{new Date(j.created_at).toLocaleString()}</div>
              <div className="text-slate-200">{j.ticket_count} tickets · {j.sheets} sheets · <span className="text-orange-300">{j.orientation || "PORTRAIT"}</span></div>
              <div className="text-slate-400">
                Start #{j.start_position}
                {j.test_alignment ? " · TEST" : ""}
                {j.template_versions && Object.keys(j.template_versions).length > 0 && (
                  <span className="ml-2 text-[10px] uppercase tracking-widest text-slate-500">
                    {Object.entries(j.template_versions).map(([k, v]) => `${k} v${v.version}`).join(" · ")}
                  </span>
                )}
              </div>
              <div className="text-slate-500 truncate">{j.actor_email}</div>
              <div className="text-right">
                <a href={`${API}/print/download/${j.id}?token=${encodeURIComponent(token)}`} className="text-orange-400 hover:underline" download>Download</a>
              </div>
            </div>
          ))}
        </div>
      </div>

      {editing && (
        <LayoutEditorModal template={editing} token={token}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadTemplates(); }} />
      )}
      {sheetEditing && (
        <SheetLayoutModal initial={sheetLayout}
          onClose={() => setSheetEditing(false)}
          onSaved={(l) => { setSheetLayout(l); setSheetEditing(false); }} />
      )}
    </div>
  );
}
